import { useHeaderHeight } from '@react-navigation/elements';
import { usePathname, useRouter } from 'expo-router';
import type { PhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js';
import React from 'react';
import { KeyboardAvoidingView, Platform, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import type { StartOtpResponse } from '@/api/customers/use-customer-start-otp';
import { useCustomerStartOtp } from '@/api/customers/use-customer-start-otp';
import { InputPhone } from '@/components/input-phone/index';
import type { PhoneCountry } from '@/components/input-phone/types';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop, validatePhoneNumber } from '@/core';
import { GREY_40 } from '@/style/colors';
import { Button, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

export default function ProfilePhone() {
  const { styles, theme } = useStyles(stylesheet);
  const headerHeight = useHeaderHeight();
  const pathname = usePathname();
  const router = useRouter();
  const [phone, setPhone] = React.useState('');
  const [country, setCountry] = React.useState<PhoneCountry>();

  const { mutate: startOtp, isPending: isStartingOtp } = useCustomerStartOtp();

  const { data: customer } = useCustomer();

  const parsedPhoneNumber = React.useMemo((): PhoneNumber | null => {
    if (!customer || !customer.phone) return null;

    const phoneNumber = parsePhoneNumber(customer.phone);

    return phoneNumber;
  }, [customer]);

  const isValidPhoneNumber = React.useMemo(() => {
    if (!country || !phone) return false;

    return validatePhoneNumber(country, phone);
  }, [country, phone]);

  React.useEffect(() => {
    setPhone(parsedPhoneNumber?.nationalNumber ?? '');
  }, [parsedPhoneNumber]);

  const handleContinue = () => {
    const formattedPhone = `+${country?.callingCode}${phone}`;

    startOtp(
      {
        phone: formattedPhone,
      },
      {
        onSuccess: (_: StartOtpResponse) => {
          if (pathname.includes('/partner')) {
            router.navigate({
              pathname: '/partner/more/confirm-phone',
              params: { phone: formattedPhone },
            });
            return;
          }

          router.push({
            pathname: '/account/confirm-phone',
            params: {
              phone: formattedPhone,
            },
          });
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  if (isStartingOtp) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{
        flexGrow: 1,
        backgroundColor: theme.colors.background_1,
        alignItems: 'center',
      }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={headerHeight}
    >
      <View style={styles.container}>
        {isDesktop() ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                if (pathname.includes('/partner')) {
                  router.navigate('/partner/more/profile');
                  return;
                }

                router.navigate('/account/profile');
              }}
            />

            <Text type="largeTitle">Phone</Text>
          </View>
        ) : null}

        <View style={styles.contentContainer}>
          <View style={{ gap: 24 }}>
            <Text
              tx="profilePhone.smsInfo"
              type="bodyRegular"
              style={{ color: GREY_40 }}
            />

            <InputPhone
              onChangeText={(text: string) => setPhone(text)}
              onCountryChange={(c: PhoneCountry) => setCountry(c)}
              defaultCountryCode={parsedPhoneNumber?.country}
              phone={parsedPhoneNumber?.nationalNumber}
            />
          </View>

          <View>
            <Button
              type="primary"
              tx="profilePhone.update"
              onPress={handleContinue}
              disabled={
                !isValidPhoneNumber ||
                customer?.phone === `+${country?.callingCode}${phone}`
              }
            />
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 16,
    gap: 16,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
  contentContainer: {
    flexGrow: 1,
    gap: 24,
    justifyContent: {
      xs: 'space-between',
      md: 'flex-start',
    },
  },
}));
